/**
 * Settings persistence.
 *
 * Same rules as the inventory: reads never throw, and anything unrecognised
 * falls back to the default rather than taking the app down. Unlike the
 * inventory, a failed *write* is swallowed too — losing a preference is a much
 * smaller problem than being unable to change one.
 */

import {
  COOKING_LEVELS,
  DEFAULT_SETTINGS,
  DIETS,
  EATING_GOALS,
  EXPIRY_ALERTS,
  THEMES,
} from "./types";
import type { Settings } from "./types";

const SETTINGS_KEY = "pantrymind.settings.v1";

/** Keeps a stored value only if it's still one of the options we offer. */
function oneOf<T extends string>(options: readonly T[], value: unknown, fallback: T): T {
  return typeof value === "string" && (options as readonly string[]).includes(value)
    ? (value as T)
    : fallback;
}

export function loadSettings(): Settings {
  try {
    const raw = localStorage.getItem(SETTINGS_KEY);
    if (!raw) return DEFAULT_SETTINGS;

    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== "object" || parsed === null) return DEFAULT_SETTINGS;
    const record = parsed as Record<string, unknown>;

    // Field by field, so one retired option can't discard the rest.
    return {
      theme: oneOf(THEMES, record.theme, DEFAULT_SETTINGS.theme),
      eating: oneOf(EATING_GOALS, record.eating, migratedEating(record)),
      diet: oneOf(DIETS, record.diet, migratedDiet(record)),
      notificationFrequency: oneOf(
        EXPIRY_ALERTS,
        record.notificationFrequency,
        DEFAULT_SETTINGS.notificationFrequency
      ),
      cookingExperience: oneOf(
        COOKING_LEVELS,
        record.cookingExperience,
        DEFAULT_SETTINGS.cookingExperience
      ),
    };
  } catch {
    return DEFAULT_SETTINGS;
  }
}

/**
 * `dietaryMode` used to hold both axes in one field: maintain / bulk / cut /
 * vegetarian / vegan. Splitting it would have quietly reset anyone who had
 * chosen one, so the old value is read once and sent to whichever field it
 * actually belonged to.
 *
 * Someone who picked "vegan" cared about being vegan, not about maintaining,
 * so their eating goal falls back to the default and their diet is preserved.
 */
function migratedEating(record: Record<string, unknown>): Settings["eating"] {
  const old = record.dietaryMode;
  return old === "bulk" || old === "cut" || old === "maintain"
    ? old
    : DEFAULT_SETTINGS.eating;
}

function migratedDiet(record: Record<string, unknown>): Settings["diet"] {
  const old = record.dietaryMode;
  return old === "vegetarian" || old === "vegan" ? old : DEFAULT_SETTINGS.diet;
}

export function saveSettings(settings: Settings): void {
  try {
    localStorage.setItem(SETTINGS_KEY, JSON.stringify(settings));
  } catch {
    // A full quota shouldn't stop someone switching to dark mode. The change
    // still applies for this session; it just won't survive a reload.
  }
}
